import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Rocket, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Bitte alle Felder ausfüllen');
      return;
    }
    
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success('Erfolgreich angemeldet!');
      
      // Check for redirect after login (e.g., from skip verification)
      const redirectAfterLogin = localStorage.getItem('redirect_after_login');
      if (redirectAfterLogin) {
        localStorage.removeItem('redirect_after_login');
        navigate(redirectAfterLogin);
      } else if (!user.subscription_status || user.subscription_status === 'none') {
        // User hasn't paid yet - go to checkout
        navigate('/checkout');
      } else if (!user.onboarding_completed) {
        navigate('/onboarding');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F2F2F7] flex">
      {/* Left side - Form */}
      <div className="flex-1 flex flex-col justify-center px-6 py-12 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Zurück
          </Link>
          
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-violet-500 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/25">
              <Rocket className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              JaySmart
            </h1>
          </div>

          <h2 className="text-3xl font-bold tracking-tight mb-2 text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Willkommen zurück
          </h2>
          <p className="text-gray-600 mb-8">
            Melde dich an, um deine Dokumente zu verwalten.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                E-Mail-Adresse
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 h-12 rounded-xl bg-white border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/20"
                placeholder="deine@email.de"
                data-testid="login-email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                Passwort
              </Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 rounded-xl bg-white border-gray-200 focus:border-[#0055FF] focus:ring-[#0055FF]/20 pr-12"
                  placeholder="••••••••"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-600 hover:to-violet-600 text-white rounded-full font-medium shadow-lg shadow-indigo-500/25"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                  Anmelden...
                </span>
              ) : (
                'Anmelden'
              )}
            </Button>
          </form>

          <p className="mt-8 text-center text-sm text-gray-600">
            Noch kein Konto?{' '}
            <Link to="/signup" className="text-indigo-600 hover:underline font-medium">
              Jetzt registrieren
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Visual */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-indigo-500 to-violet-500 items-center justify-center p-12">
        <div className="max-w-md text-white">
          <h2 className="text-4xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Alle Rechnungen im Griff.
          </h2>
          <p className="text-lg text-white/80 leading-relaxed">
            KI-gestützte Dokumentenerkennung, automatische Fristen-Erinnerungen 
            und ein intuitives Kanban-Board für deinen Workflow.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
